import { useState, useRef, useCallback } from "react";
import { Upload, FileText, X, Check, AlertCircle, ChevronDown } from "lucide-react";
import api from "../services/api";
import { errorToUserMessage } from "../services/errorMessages";

const DB_FIELDS = [
  { value: "phone_number", label: "Phone Number" },
  { value: "first_name", label: "First Name" },
  { value: "last_name", label: "Last Name" },
  { value: "skip", label: "— Skip Column —" },
];

export default function ContactUpload({ campaignId, onUploadStarted, onClose }) {
  const [step, setStep] = useState("upload"); // upload, mapping, confirm
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [fieldMapping, setFieldMapping] = useState({});
  const [consentAttested, setConsentAttested] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    const dropped = e.dataTransfer?.files?.[0];
    if (dropped) handleFile(dropped);
  }, []);

  const handleFile = async (selectedFile) => {
    setFile(selectedFile);
    setError(null);

    // Preview the file
    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const res = await api.post("/contacts/upload/preview", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setPreview(res.data);

      // Auto-detect field mapping from detected_mapping
      const detected = res.data.detected_mapping || {};
      const initial = {};
      res.data.headers.forEach((h) => {
        const normalized = h.toLowerCase().replace(/\s+/g, "_");
        if (detected[h]) {
          initial[h] = detected[h];
        } else if (normalized.includes("phone")) {
          initial[h] = "phone_number";
        } else if (normalized.includes("first")) {
          initial[h] = "first_name";
        } else if (normalized.includes("last")) {
          initial[h] = "last_name";
        } else {
          initial[h] = h.toLowerCase().replace(/\s+/g, "_");
        }
      });
      setFieldMapping(initial);
      setStep("mapping");
    } catch (err) {
      setError(errorToUserMessage(err, "Failed to preview file"));
    }
  };

  const handleUpload = async () => {
    if (!consentAttested) {
      setError("You must attest that contacts have given prior consent.");
      return;
    }
    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      if (campaignId) formData.append("campaign_id", campaignId);
      formData.append("consent_attested", "true");
      Object.entries(fieldMapping).forEach(([csv, db]) => {
        formData.append(`field_map[${csv}]`, db);
      });

      const res = await api.post("/contacts/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      onUploadStarted?.(res.data.contact_list);
      setStep("upload");
      setFile(null);
      setPreview(null);
    } catch (err) {
      setError(errorToUserMessage(err, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const updateMapping = (header, value) => {
    setFieldMapping((prev) => ({ ...prev, [header]: value }));
  };

  // ── Upload Step ──
  if (step === "upload") {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Upload Contacts</h3>
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        <div
          className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors ${
            dragActive
              ? "border-blue-500 bg-blue-50"
              : "border-gray-300 hover:border-blue-400 hover:bg-gray-50"
          }`}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
          <p className="text-sm text-gray-600 mb-1">
            Drag and drop your CSV file here, or click to browse
          </p>
          <p className="text-xs text-gray-400">
            Supports CSV, TSV, and TXT files up to 50MB
          </p>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.tsv,.txt"
          className="hidden"
          onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
        />

        {error && (
          <div className="mt-3 flex items-center gap-2 text-red-600 text-sm">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}
      </div>
    );
  }

  // ── Mapping Step ──
  if (step === "mapping" && preview) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Map Columns</h3>
          <button onClick={() => { setStep("upload"); setFile(null); }} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex items-center gap-2 mb-4 text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
          <FileText className="w-4 h-4" />
          <span className="font-medium">{preview.filename}</span>
          <span className="text-gray-400">—</span>
          <span>{preview.total_rows} contacts found</span>
          {preview.error_count > 0 && (
            <span className="text-amber-600">({preview.error_count} with errors)</span>
          )}
        </div>

        {/* Column mapping */}
        <div className="space-y-3 mb-6">
          <p className="text-sm font-medium text-gray-700">Match your CSV columns to contact fields:</p>
          {preview.headers.map((header) => (
            <div key={header} className="flex items-center gap-3">
              <span className="w-40 text-sm text-gray-600 truncate font-mono bg-gray-100 px-2 py-1 rounded">
                {header}
              </span>
              <span className="text-gray-400">→</span>
              <select
                value={fieldMapping[header] || ""}
                onChange={(e) => updateMapping(header, e.target.value)}
                className="flex-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {DB_FIELDS.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
                <option value={header.toLowerCase().replace(/\s+/g, "_")}>
                  Custom: {header}
                </option>
              </select>
            </div>
          ))}
        </div>

        {/* Preview table */}
        {preview.preview_rows.length > 0 && (
          <div className="mb-6">
            <p className="text-sm font-medium text-gray-700 mb-2">Preview (first 5 rows):</p>
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50">
                    {preview.headers.map((h) => (
                      <th key={h} className="px-3 py-2 text-left font-medium text-gray-600">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.preview_rows.map((row, i) => (
                    <tr key={i} className="border-t">
                      {preview.headers.map((h) => (
                        <td key={h} className="px-3 py-1.5 text-gray-700">
                          {row[h.toLowerCase().replace(/\s+/g, "_")] || row.phone_number || "—"}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Consent attestation */}
        <label className="flex items-start gap-3 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer">
          <input
            type="checkbox"
            checked={consentAttested}
            onChange={(e) => setConsentAttested(e.target.checked)}
            className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-amber-800">
            I attest that all contacts in this list have given prior express consent
            to receive text messages, in compliance with TCPA regulations.
          </span>
        </label>

        {error && (
          <div className="mb-4 flex items-center gap-2 text-red-600 text-sm">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button
            onClick={() => { setStep("upload"); setFile(null); }}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={uploading || !consentAttested}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {uploading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Upload {preview.total_rows} Contacts
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  return null;
}
